import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import HeroSection from './components/HeroSection';
import MeetTheTeamSection from './components/MeetTheTeamSection';
import WhoWeAreSection from './components/WhoWeAreSection';
import FoundersPrinciplesSection from './components/FoundersPrinciplesSection';
import HomeownerBenefitsSection from './components/HomeownerBenefitsSection';
import WhereWereGoingSection from './components/WhereWereGoingSection';
import DifferentiatorsSection from './components/DifferentiatorsSection';
import TrustQualitySection from './components/TrustQualitySection';
import WhoItsForSection from './components/WhoItsForSection';
import MissionVisionSection from './components/MissionVisionSection';
import FinalCTASection from './components/FinalCTASection';
import Footer from '../../components/Footer';

const AboutSEOSection = () => (
  <section className="py-16 px-4 bg-gray-50 border-t border-gray-200">
    <div className="max-w-5xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">About OpenbooQ — Roofing Reimagined for Homeowners Nationwide</h2>
      <p className="text-lg text-gray-600 leading-relaxed mb-5">
        OpenbooQ was founded on a simple but powerful idea: homeowners deserve to know exactly what they're paying for when they replace their roof. The traditional roofing industry has long operated on opacity — vague estimates, commissioned salespeople, and markups hidden inside "package pricing." OpenbooQ was built to change that.
      </p>
      <p className="text-lg text-gray-600 leading-relaxed mb-5">
        Our platform combines satellite-based roof measurement technology, real-time material pricing from top shingle manufacturers, and a vetted network of certified local installers to deliver a roofing experience that is transparent, efficient, and genuinely fair. Every roof price we generate shows you exactly what you're paying for materials, labor, and our single 20% Service & Warranty fee — with no hidden markups anywhere in the process.
      </p>
      <div className="grid md:grid-cols-2 gap-8 mt-8">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Mission</h3>
          <p className="text-gray-600 leading-relaxed">
            To make roof replacement as transparent, accessible, and stress-free as possible for every homeowner. We believe that when people have accurate information and honest pricing, they make better decisions — and they end up with better roofs. Our mission is to be the platform that makes that possible at scale, across every market we serve.
          </p>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Standards</h3>
          <p className="text-gray-600 leading-relaxed">
            Every contractor in the OpenbooQ network is vetted for licensing, insurance, installation quality, and customer service standards. We only partner with installers who share our commitment to transparency and craftsmanship — because the best pricing in the world doesn't matter if the installation isn't done right. Our quality assurance process includes pre-installation verification and post-installation inspection on every project.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const AboutUs = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://openbooq.com/#organization",
    "name": "OpenbooQ",
    "url": "https://openbooq.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://openbooq.com/assets/images/Openbooq_blue-1773069231613.png",
      "width": 400,
      "height": 120
    },
    "description": "OpenbooQ was founded to bring radical transparency to the roofing industry. We combine satellite-based roof measurement technology, real-time material pricing, and a vetted network of certified local installers to deliver a roofing experience that is transparent, efficient, and genuinely fair.",
    "foundingDate": "2024",
    "foundingLocation": {
      "@type": "Place",
      "name": "United States"
    },
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "knowsAbout": ["Roof Replacement", "Roofing Materials", "Shingles", "Home Improvement"],
    "slogan": "Buy Your Roof Online. Yeah, seriously.",
    "sameAs": [
      "https://www.facebook.com/openbooq",
      "https://www.instagram.com/openbooq",
      "https://www.linkedin.com/company/openbooq"
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://openbooq.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "About Us",
        "item": "https://openbooq.com/about-us"
      }
    ]
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="min-h-screen bg-white">
        <Header />
        
        <main>
          <HeroSection />
          <MeetTheTeamSection />
          <WhoWeAreSection />
          <MissionVisionSection />
          <FoundersPrinciplesSection />
          <HomeownerBenefitsSection />
          <WhereWereGoingSection />
          <DifferentiatorsSection />
          <TrustQualitySection />
          <WhoItsForSection />
          <AboutSEOSection />
          <FinalCTASection />
        </main>

        <Footer />
      </div>
    </>
  );
};

export default AboutUs;